var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import * as yup from 'yup';
import config from '../../config';
const breadcrumbSchema = yup.object({
    entityId: yup.string().required(),
    name: yup.string().required()
});
const locationSchema = yup.object({
    id: yup.object({ entityId: yup.string().required() }),
    name: yup.string().required(),
    breadcrumbs: yup.array(breadcrumbSchema).optional(),
});
const locationsSchema = yup.object({ _embedded: yup.array(locationSchema) });
const checkSchema = yup.object({
    id: yup.object({
        entityId: yup.string().required(),
        version: yup.number().required(),
    }),
    code: yup.string().required(),
    name: yup.string().required(),
});
const checksSchema = yup.object({ _embedded: yup.array(checkSchema) });
const meSchema = yup.object({
    _embedded: yup.object({
        customer: yup.object({
            automatedMonitoringEnabled: yup.boolean().required(),
            workManagementEnabled: yup.boolean().required(),
            name: yup.string().required()
        })
    }).required(),
    _meta: yup.object({
        administratesEntityIds: yup.array().optional(),
        safetyManagesEntityIds: yup.array().optional(),
        supervisesEntityIds: yup.array().optional()
    }).required(),
    email: yup.string().required(),
    name: yup.string().required(),
    id: yup.string().required()
}).required();
export function backendClientFactory(getToken) {
    const axiosInstance = axios.create({
        baseURL: config.backend.url,
        responseType: 'json'
    });
    function getHeaders() {
        return __awaiter(this, void 0, void 0, function* () {
            const token = yield getToken();
            return { Authorization: `Bearer ${token}` };
        });
    }
    function getLocations(text, limit) {
        return __awaiter(this, void 0, void 0, function* () {
            const regexEscapedText = text.toLowerCase().replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
            const regex = new RegExp(regexEscapedText, 'i');
            const headers = yield getHeaders();
            const res = yield axiosInstance.get('/locations', {
                headers,
                params: {
                    filter: JSON.stringify({
                        fields: {
                            // We don't care about this but the backend returns a 500 if we don't include it (sigh)
                            _descendants: true,
                            breadcrumbs: true,
                            id: true,
                            name: true
                        },
                        limit,
                        order: 'name ASC',
                        where: {
                            name: { regexp: regex.toString() }
                        }
                    })
                },
            });
            const validated = locationsSchema.validateSync(res.data);
            const mappedLocations = validated._embedded.map((x) => {
                return {
                    code: x.id.entityId,
                    name: x.name,
                    breadcrumbs: x.breadcrumbs.map((y) => ({ code: y.entityId, name: y.name }))
                };
            });
            return mappedLocations;
        });
    }
    function getChecks(text, limit) {
        return __awaiter(this, void 0, void 0, function* () {
            const regexEscapedText = text.toLowerCase().replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
            const regex = new RegExp(regexEscapedText, 'i');
            const headers = yield getHeaders();
            const res = yield axiosInstance.get('/checks', {
                headers,
                params: {
                    filter: JSON.stringify({
                        limit,
                        order: 'name ASC',
                        where: {
                            name: { regexp: regex.toString() },
                            isRoot: true
                        }
                    })
                },
            });
            const validated = checksSchema.validateSync(res.data);
            return validated._embedded;
        });
    }
    function getMe() {
        return __awaiter(this, void 0, void 0, function* () {
            const headers = yield getHeaders();
            const res = yield axiosInstance.get('users/me', { headers, params: { filter: { include: 'customer' } } });
            const validated = meSchema.validateSync(res.data);
            const { name, email, _meta, _embedded, id } = validated;
            const { administratesEntityIds, safetyManagesEntityIds, supervisesEntityIds } = _meta;
            const { automatedMonitoringEnabled, workManagementEnabled } = _embedded.customer;
            const roles = {
                administrator: Boolean(administratesEntityIds && administratesEntityIds.length > 0),
                safetyManager: Boolean(safetyManagesEntityIds && safetyManagesEntityIds.length > 0),
                supervisor: Boolean(supervisesEntityIds && supervisesEntityIds.length > 0)
            };
            const modules = {
                automatedMonitoring: automatedMonitoringEnabled,
                workManagement: workManagementEnabled
            };
            const customer = {
                name: _embedded.customer.name
            };
            return { name, email, roles, modules, customer, code: id };
        });
    }
    return {
        getMe,
        getLocations,
        getChecks
    };
}
//# sourceMappingURL=backendClient.js.map